import { createServerFn } from '@tanstack/react-start'
import { getSupabaseAdminClient } from '~/server/common/bindings'
import { log } from '~/server/common/logging'
import { getShareableListBySlug, type ShareableListData } from '~/server/shareable-list/shareable-list.service'
import { EventTypeChoices } from '~/shared/common/enums'

export type ShareableListMeta = {
  name: string
  criteria: string | null
  location: string | null
  authorName: string | null
  authorTitle: string | null
} | null

type AuthorJoin =
  | { first_name: string | null; last_name: string | null; name: string | null; title: string | null }
  | null
  | undefined

function deriveAuthor(raw: unknown): { name: string | null; title: string | null } {
  const author = (Array.isArray(raw) ? raw[0] : raw) as AuthorJoin
  if (!author) return { name: null, title: null }
  const fullName = [author.first_name, author.last_name].filter(Boolean).join(' ').trim()
  return {
    name: fullName || author.name || null,
    title: author.title ?? null,
  }
}

async function getShareableListMetaInternal(slug: string): Promise<ShareableListMeta> {
  const client = getSupabaseAdminClient()
  const { data } = await client
    .from('crm_eventspacelist')
    .select('name, criteria, location, author:update_author_id(first_name, last_name, name, title)')
    .eq('slug', slug)
    .eq('event_type', EventTypeChoices.SHAREABLE_LIST)
    .is('deleted_at', null)
    .maybeSingle()

  if (!data) return null

  const author = deriveAuthor(data.author)
  return {
    name: data.name,
    criteria: data.criteria,
    location: data.location,
    authorName: author.name,
    authorTitle: author.title,
  }
}

function isNotFoundError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err)
  return (
    message.includes('not found') ||
    message.includes('PGRST116') ||
    message.includes('0 rows')
  )
}

export const fetchShareableListMeta = createServerFn()
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<ShareableListMeta> => getShareableListMetaInternal(slug))

export type ShareableListPageData = {
  meta: ShareableListMeta
  data: ShareableListData | null
}

export const fetchShareableListPageData = createServerFn()
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<ShareableListPageData> => {
    const client = getSupabaseAdminClient()
    try {
      const data = await getShareableListBySlug(slug, client)
      const author = deriveAuthor((data as { author?: unknown }).author)
      const meta: ShareableListMeta = {
        name: data.name,
        criteria: data.criteria,
        location: data.location,
        authorName: author.name,
        authorTitle: author.title,
      }
      return { meta, data }
    } catch (err) {
      if (isNotFoundError(err)) {
        return { meta: null, data: null }
      }
      // Operational failure (e.g. Typesense timeout, missing env, follow-up Supabase
      // error). Log it and fall back to a meta-only fetch so OG tags / SEO still
      // work; the body will render the generic not-found state.
      log.error('[fetchShareableListPageData] full fetch failed, falling back to meta-only', {
        scope: 'fetchShareableListPageData',
        slug,
        error: err instanceof Error ? { message: err.message, stack: err.stack } : err,
      })
      const meta = await getShareableListMetaInternal(slug).catch(() => null)
      return { meta, data: null }
    }
  })
